import axios from "axios";
import { format as formatMoney } from "@/lib/money";

// Resolve backend URL with a same-origin fallback that's resilient to
// stale build-time REACT_APP_BACKEND_URL values (e.g. when deploying behind a
// custom domain after the production bundle was built with the original
// deployment slug). Same-origin avoids CORS + cross-site cookie issues.
function resolveBackendUrl() {
  const envUrl = process.env.REACT_APP_BACKEND_URL || "";
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    const origin = window.location.origin;
    // In the browser, prefer the current origin unless we're on localhost
    // (dev) and the env var points at a remote backend (CRA local dev).
    const isLocalhost = /^https?:\/\/(localhost|127\.0\.0\.1)(:|$)/.test(origin);
    if (!isLocalhost) return origin;
    // Localhost dev: fall back to env var so CRA/craco can hit the deployed
    // backend through REACT_APP_BACKEND_URL.
    return envUrl || origin;
  }
  return envUrl;
}

const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

export function formatApiError(detail) {
  if (detail == null) return "A apărut o eroare. Încearcă din nou.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const STATUS_LABELS = {
  disponibila: "Disponibilă",
  rezervata: "Rezervată",
  vanduta: "Vândută",
  in_pregatire: "În pregătire",
  in_transport: "În transport",
};

export const STATUS_COLORS = {
  disponibila: { bg: "bg-emerald-50", text: "text-emerald-700", dot: "bg-emerald-500" },
  rezervata:   { bg: "bg-amber-50",   text: "text-amber-700",   dot: "bg-amber-500" },
  vanduta:     { bg: "bg-blue-50",    text: "text-blue-700",    dot: "bg-blue-600" },
  in_pregatire:{ bg: "bg-slate-100",  text: "text-slate-700",   dot: "bg-slate-400" },
  in_transport:{ bg: "bg-orange-50",  text: "text-orange-700",  dot: "bg-orange-500" },
};

export const DOCUMENTS_STATUS = [
  { value: "pending",  label: "Acte în așteptare", short: "Acte așteptare", emoji: "⏳", bg: "bg-amber-50",    text: "text-amber-700",    border: "border-amber-200" },
  { value: "cmr_sent", label: "CMR trimis",         short: "CMR trimis",     emoji: "📨", bg: "bg-sky-50",       text: "text-sky-700",      border: "border-sky-200" },
  { value: "arrived",  label: "Acte sosite",        short: "Acte sosite",    emoji: "✅", bg: "bg-emerald-50",   text: "text-emerald-700",  border: "border-emerald-200" },
];

export const DOCUMENTS_STATUS_MAP = Object.fromEntries(DOCUMENTS_STATUS.map((d) => [d.value, d]));

export const SOURCES = ["OLX", "Autovit", "Google", "Facebook", "Instagram", "Recomandare", "Walk-in", "Site web", "Altele"];

// Cele 42 de județe ale României (41 + București)
export const JUDETE = [
  "Alba", "Arad", "Argeș", "Bacău", "Bihor", "Bistrița-Năsăud", "Botoșani",
  "Brașov", "Brăila", "București", "Buzău", "Caraș-Severin", "Călărași",
  "Cluj", "Constanța", "Covasna", "Dâmbovița", "Dolj", "Galați", "Giurgiu",
  "Gorj", "Harghita", "Hunedoara", "Ialomița", "Iași", "Ilfov", "Maramureș",
  "Mehedinți", "Mureș", "Neamț", "Olt", "Prahova", "Satu Mare", "Sălaj",
  "Sibiu", "Suceava", "Teleorman", "Timiș", "Tulcea", "Vaslui", "Vâlcea",
  "Vrancea",
];

export const TODO_PRIORITY = [
  { value: "low",    label: "Scăzut", color: "#94A3B8" },
  { value: "normal", label: "Normal", color: "#0047FF" },
  { value: "high",   label: "Urgent", color: "#EF4444" },
];
export const TODO_PRIORITY_MAP = Object.fromEntries(TODO_PRIORITY.map((p) => [p.value, p]));

export const EXPENSE_CATEGORIES = [
  "Achiziție",
  "Transport",
  "Reparații",
  "Vopsit",
  "Polish/Detailing",
  "Piese",
  "Service",
  "Garanție",
  "R.A.R",
  "Taxe & Înmatriculare",
  "Comision",
  "Altele",
];

export const FIXED_COST_CATEGORIES = [
  "Chirie",
  "Utilități",
  "Salarii",
  "Leasing",
  "Contabilitate",
  "Marketing",
  "Asigurări",
  "Altele",
];

export const VAT_STATUS = [
  { value: "cu_tva", label: "Cu TVA" },
  { value: "fara_tva", label: "Fără TVA" },
  { value: "fara_factura", label: "Fără factură" },
];

export const VAT_LABEL = {
  cu_tva: "Cu TVA",
  fara_tva: "Fără TVA",
  fara_factura: "Fără factură",
};

export const INTERACTION_TYPES = [
  { value: "call", label: "Telefon" },
  { value: "message", label: "Mesaj" },
  { value: "visit", label: "Vizită showroom" },
  { value: "test_drive", label: "Test drive" },
  { value: "offer", label: "Ofertă" },
];

export const EVENT_TYPES = [
  { value: "call",       label: "Apel telefonic", color: "#0EA5E9", emoji: "📞" },
  { value: "meeting",    label: "Întâlnire",       color: "#8B5CF6", emoji: "🤝" },
  { value: "test_drive", label: "Test drive",      color: "#F59E0B", emoji: "🚗" },
  { value: "delivery",   label: "Livrare",         color: "#10B981", emoji: "📦" },
  { value: "follow_up",  label: "Follow-up",       color: "#EF4444", emoji: "🔄" },
  { value: "custom",     label: "Altele",          color: "#0047FF", emoji: "✏️" },
];

export const EVENT_TYPE_MAP = Object.fromEntries(EVENT_TYPES.map((t) => [t.value, t]));

export const EVENT_STATUS = [
  { value: "scheduled", label: "Programat" },
  { value: "done",      label: "Finalizat" },
  { value: "cancelled", label: "Anulat" },
];

export function ron(value) {
  return formatMoney(value);
}
